/**
 * Resolves a Firebase Storage path to a signed download URL.
 *
 * Tracks loading/error state and guards against state updates after
 * unmount or when the source path changes.
 *
 * @module useResolvedStorageUrl
 */
import { useEffect, useState } from "react";
import { getFileDownloadUrl } from "../api/firebase/firestoreService";
import { logError } from "../utils/customLogger";

/** Return value of {@link useResolvedStorageUrl}. */
interface UseResolvedStorageUrlState {
  /** Signed HTTPS URL when resolution succeeds; empty while loading or on error */
  resolvedUrl: string;
  /** True while `getFileDownloadUrl` is in flight */
  isLoading: boolean;
  /** User-safe message when resolution fails; null on success */
  loadError: string | null;
}

/**
 * Resolves a Firebase Storage path to a signed download URL.
 *
 * @param storagePath - Firebase Storage path to resolve; empty string
 *   disables resolution
 * @returns Resolved URL plus loading/error state for the caller
 */
export const useResolvedStorageUrl = (
  storagePath: string,
): UseResolvedStorageUrlState => {
  const [resolvedUrl, setResolvedUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setResolvedUrl("");
    setLoadError(null);

    if (!storagePath) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const resolveUrl = async () => {
      try {
        const downloadUrl = await getFileDownloadUrl(storagePath);
        if (cancelled) {
          return;
        }
        if (!downloadUrl) {
          setLoadError("Unable to resolve the clinical document URL.");
          return;
        }
        setResolvedUrl(downloadUrl);
      } catch (error) {
        if (cancelled) {
          return;
        }
        logError(
          error as Error,
          "Failed to resolve clinical document download URL",
        );
        setLoadError("Unable to load the clinical document.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void resolveUrl();

    return () => {
      cancelled = true;
    };
  }, [storagePath]);

  return { resolvedUrl, isLoading, loadError };
};
